<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import MaskInput from 'svelte-input-mask'
    import { derived as sderived } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'
    import type { DateTime } from 'luxon'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as userGroups, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'
    import { makeSortString } from 'hydria/utils/strings'
    import { toLuxon } from 'hydria/utils/time'

    import type { IDomain } from 'wdom/domain/interfaces'
    import { loadFrequentTags, type FTag } from 'admin/helpers/tags'

    import type { DomainHandler } from './domain'

    import Date from 'hydria/components/Date.svelte'
    import Dropdown from 'hydria/components/Dropdown.svelte'
    import PopEntry from 'hydria/components/PopEntry.svelte'
    import IntCurrency from 'wdom/components/IntCurrency.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const Domain = $state(getContext('Domain') as DomainHandler)
    const status = sderived(Domain.status, ($status) => $status)

    let domain: IDomain = $derived($status == 'ready' ? Domain.domain : ({} as IDomain))

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                       Unactivated Years / Expiration
    // ╰─────────────────────────────────────────────────────────────────────┄

    let unactivatedYears: number
    let missingYearCanBeActivated: boolean = $state(false)
    let missingYearCanBeActivatedFrom: DateTime | undefined = $state()
    let missingYearWorking = $state(false)

    function importMissingYearData() {
        const data = Domain.raw
        unactivatedYears = data.item.unactivated_years ? data.item.unactivated_years : 0
        missingYearCanBeActivated = Boolean(data.missing_year_can_be_activated)
        missingYearCanBeActivatedFrom = !missingYearCanBeActivated && unactivatedYears > 0 ? toLuxon(data.missing_year_can_be_activated_from) : undefined
    }

    async function activateMissingYear() {
        missingYearWorking = true
        try {
            await simpleFetch('POST', `/json/domain/1/${domain.idna}/activate-missing-year`)
            try {
                // @ts-expect-error
                Domain.raw.item.unactivated_years--
                // @ts-expect-error
                Domain.domain.unactivated_years--
            } catch {}
            Domain.raw.missing_year_can_be_activated = false
            importMissingYearData()
        } catch {}
        missingYearWorking = false
    }

    let specialWorking = $state(false)
    interface ISpecial {
        expiration: string | undefined
    }
    let special: ISpecial = $state({
        expiration: undefined,
    })

    async function saveFakeExpiration(value: string | undefined) {
        specialWorking = true
        if (specialOpen) specialOpen = false
        const specialEdit = { ...special }
        specialEdit.expiration = value
        try {
            const r = await simpleFetch('POST', `/json/domain/1/${domain.idna}/fake-expiration`, { ...specialEdit })
            Domain.domain.fake_expiration = r.data.expiration
            Domain.setupExpiration()
            specialWorking = false
        } catch {
            specialWorking = false
        }
    }

    let specialOpen = $state(false)
    let specialValue: string | undefined = $derived.by(() => {
        specialWorking
        return $status == 'ready' && domain.fake_expiration ? domain.fake_expiration.toFormat('yyyy-MM-dd') : undefined
    })
    let specialValueEdit: string | undefined = $state()

    function openSpecial() {
        special.expiration = Domain.domain.fake_expiration != null ? Domain.domain.fake_expiration.toFormat('yyyy-MM-dd') : undefined
        specialValueEdit = special.expiration ? special.expiration : ''
        specialOpen = true
    }
    function specialIsValid(value) {
        return value == null || Boolean(value && value.match(/^20[0-9]{2}-(0[1-9]|1[0-2])-[0-3][0-9]$/))
    }
    // $: specialValid = Boolean(specialEdit.expiration && specialEdit.expiration.match(/^20[0-9]{2}-(0[1-9]|1[0-2])-[0-3][0-9]$/))

    $effect(() => {
        if ($status == 'ready') {
            importMissingYearData()
        }
    })

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                                  EPP
    // ╰─────────────────────────────────────────────────────────────────────┄

    let eppLoading = $state(false)
    let epp: string | undefined = $state()

    async function loadEpp() {
        eppLoading = true
        try {
            const r = await simpleFetch('GET', `/json/domain/1/${domain.idna}/epp-auth-password`)
            epp = r.data.epp_auth_password
        } catch {
            epp = undefined
        }
        eppLoading = false
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                                 Sync
    // ╰─────────────────────────────────────────────────────────────────────┄

    const syncTaskRunning = sderived(Domain.runningTasks.sync, ($v) => $v)
    let localSync = $state(false)

    async function doSync() {
        localSync = true
        try {
            const r = await simpleFetch('POST', `/json/domain/1/${domain.idna}/update-local`)

            notify({
                type: 'success',
                title: $_('Domain update'),
                text: $_('Domain update task has been queued.'),
            })
        } catch {
            notify({
                type: 'danger',
                title: $_('Domain update'),
                text: $_('Unknown error'),
            })
            localSync = false
        }
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                           Properties
    // ╰─────────────────────────────────────────────────────────────────────┄

    // const whoisPrivacyTaskRunning = sderived(Domain.runningTasks.whoisPrivacy, ($v) => $v)
    let whoisPrivacyTaskRunning = $state(false)
    let whoisPrivacyActive: boolean = $state(false)
    let togglingWhois = $state(false)

    unsubs.push(
        Domain.runningTasks.whoisPrivacy.subscribe((v) => {
            whoisPrivacyActive = Boolean(domain.whois_privacy)
            whoisPrivacyTaskRunning = v
        }),
    )

    let registrarLockTaskRunning = $state(false)
    let locked: boolean = $state(false)
    unsubs.push(
        Domain.runningTasks.registrarLock.subscribe((v) => {
            locked = Boolean(domain.locked)
            registrarLockTaskRunning = v
        }),
    )

    let clientDeleteProhibitedTaskRunning = $state(false)
    let clientDeleteProhibited: boolean = $state(false)
    unsubs.push(
        Domain.runningTasks.clientDeleteProhibited.subscribe((v) => {
            clientDeleteProhibited = Boolean(domain.cira_status != null && domain.cira_status.includes('clientDeleteProhibited'))
            clientDeleteProhibitedTaskRunning = v
        }),
    )

    let clientUpdateProhibitedTaskRunning = $state(false)
    let clientUpdateProhibited: boolean = $state(false)
    unsubs.push(
        Domain.runningTasks.clientUpdateProhibited.subscribe((v) => {
            clientUpdateProhibited = Boolean(domain.cira_status != null && domain.cira_status.includes('clientUpdateProhibited'))
            clientUpdateProhibitedTaskRunning = v
        }),
    )

    let clientRenewProhibitedTaskRunning = $state(false)
    let clientRenewProhibited: boolean = $state(false)
    unsubs.push(
        Domain.runningTasks.clientRenewProhibited.subscribe((v) => {
            clientRenewProhibited = Boolean(domain.cira_status != null && domain.cira_status.includes('clientRenewProhibited'))
            clientRenewProhibitedTaskRunning = v
        }),
    )

    let clientHoldTaskRunning = $state(false)
    let clientHold: boolean = $state(false)
    unsubs.push(
        Domain.runningTasks.clientHold.subscribe((v) => {
            clientHold = Boolean(domain.cira_status != null && domain.cira_status.includes('clientHold'))
            clientHoldTaskRunning = v
        }),
    )

    let confirmModal: false | 'clearWhois' | 'setWhois' | 'delete' | 'tag-add' | 'tag-remove' = $state(false)
    $effect(() => {
        if (confirmModal && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!confirmModal && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
    onDestroy(() => {
        if (confirmModal !== false && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                        Owner/Comment
    // ╰─────────────────────────────────────────────────────────────────────┄

    let ownerName: string | undefined = $state(undefined)
    let accountingComment: string | undefined = $state(undefined)

    async function importOwnerName() {
        try {
            const r = await simpleFetch('GET', `/json/wdom-user/1/${domain.owner}/name?with_accounting_comment=1`)
            ownerName = r.data.name ? r.data.name : r.data.org ? r.data.org : '?'
            // if (r.data.org && r.data.name) ownerName = `${r.data.name} (${r.data.org})`
            // else if (r.data.name) ownerName = r.data.name
            // else if (r.data.org) ownerName = `? (${r.data.org})`
            // else ownerName = 'inconnu'
            accountingComment = r.data.accounting_comment
        } catch {
            ownerName = 'inconnu'
        }
    }

    $effect(() => {
        if ($status == 'ready') {
            importOwnerName()
        }
    })

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                        Delete Domain
    // ╰─────────────────────────────────────────────────────────────────────┄

    let deletingDomain = $state(false)
    let deleteReason: string = $state('')
    let cleanDeleteReason = $derived(deleteReason ? deleteReason.trim() : '')

    async function deleteDomain() {
        deletingDomain = true
        confirmModal = false
        try {
            const r = await simpleFetch('DELETE', `/json/domain/1/${domain.idna}`, { reason: cleanDeleteReason })
            notify({
                type: 'success',
                title: $_('Domain deletion'),
                text: $_('Domain successfully deleted.'),
            })
            sgoto(`/wdom-user/${domain.owner}`)
        } catch {
            notify({
                type: 'danger',
                title: $_('Domain deletion'),
                text: 'Unknown error',
            })
            deletingDomain = false
        }
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                                Order
    // ╰─────────────────────────────────────────────────────────────────────┄

    let creatingOrder = $state(false)
    async function createOrder(withDomain = false) {
        creatingOrder = true
        let opts = { user_id: domain.owner }
        if (withDomain) {
            opts['items'] = [{ type: 'domain', renewal: true, domain: Domain.idna }]
        }
        try {
            const r = await simpleFetch('POST', '/json/ecom/1/order', opts)

            sgoto(`/ecom/order/${r.data.order.id}`)
        } catch {
            creatingOrder = false
            notify({
                type: 'danger',
                title: 'Order',
                text: 'Failed to create new order',
            })
        }
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                                 Tags
    // ╰─────────────────────────────────────────────────────────────────────┄

    let globalFrequentTags: FTag[] | undefined = $state(undefined)
    loadFrequentTags('domain').then((r) => {
        globalFrequentTags = r
    })

    let frequentTags: FTag[] | undefined = $state(undefined)

    function prepLocalFrequentTags(globalFrequentTags: FTag[], domTags: string[]) {
        const assignedTags = new Set(domTags)
        frequentTags = globalFrequentTags.filter((ftag) => !assignedTags.has(ftag.id))
    }

    let tagUpdateSignal = $state(1)
    let sortedTags: string[] = $derived($status == 'ready' && tagUpdateSignal && domain.tags != null && domain.tags.length ? orderBy([...new Set(domain.tags)], [(v) => makeSortString(v)], ['asc']) : [])
    $effect(() => {
        if (sortedTags != null && globalFrequentTags != null) prepLocalFrequentTags(globalFrequentTags, sortedTags)
    })

    let tagToRemove: string | undefined = $state(undefined)
    let tagToAdd: string = $state('')
    let tagWorking: boolean = $state(false)
    let tagInputElement: HTMLInputElement | undefined = $state()

    $effect(() => {
        if (tagInputElement) tagInputElement.focus()
    })

    function preRemoveTag(tag: string) {
        tagToRemove = tag
        confirmModal = 'tag-remove'
    }

    function preAddTag() {
        tagToAdd = ''
        confirmModal = 'tag-add'
    }

    async function removeTag(tag: string | undefined) {
        if (tag == null) return
        const nTags = new Set(sortedTags)
        nTags.delete(tag)

        try {
            await saveTags(nTags)
            notify({
                type: 'success',
                title: 'Tag',
                text: `Le tag « ${tag} » a été supprimé.`,
            })
            confirmModal = false
        } catch (error) {
            notify({
                type: 'danger',
                title: 'Tag',
                text: `Le tag « $${tag} » n’a pas pu être enlevé: ${error}`,
            })
        }
    }

    async function addTag(tag: string) {
        const nTags = new Set(sortedTags)
        nTags.add(tag)

        try {
            await saveTags(nTags)
            notify({
                type: 'success',
                title: 'Tag',
                text: `Le tag « ${tag} » a été ajouté.`,
            })
            confirmModal = false
        } catch (error) {
            notify({
                type: 'danger',
                title: 'Tag',
                text: `Le tag « ${tag} » n’a pas pu être ajouté: ${error}`,
            })

            if (tagInputElement) tagInputElement.focus()
        }
    }

    async function saveTags(nTags: Set<string>) {
        tagWorking = true

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', `/json/domain/1/${domain.idna}/tags`, { tags: Array.from(nTags) })
        } catch (e: any) {
            r = e
        }

        if (r && r.success) {
            domain.tags = r.data.tags
            tagUpdateSignal++
        } else {
            tagWorking = false
            throw r && r.data && r.data.error ? r.data.error : 'Erreur inconnue'
        }
        tagWorking = false
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                           Categories
    // ╰─────────────────────────────────────────────────────────────────────┄

    interface Category {
        id: string | null
        name: string | null
        count: number
        domains: string[]
    }

    let categoriesReady: boolean | null = $state(null)
    let categories: Category[] = $state([])
    let domainCategory: Category | undefined = $state()

    let selectCategory: undefined | Category = $state()
    let updatingCategory: boolean = $state(false)

    let categoryDirty: boolean = $derived((domainCategory != null && selectCategory == null) || (domainCategory == null && selectCategory != null) || (domainCategory != null && selectCategory != null && domainCategory.id != selectCategory.id))

    async function loadCategory() {
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', `/json/domain/1/dcat/${domain.owner}/domain/${Domain.idna}`)
        } catch (e: any) {
            r = e
        }
        if (!r || !r.data || !r.data.success) {
            categoriesReady = false
            return
        }

        categories = r.data.categories
        domainCategory = r.data.category
        selectCategory = r.data.category

        categoriesReady = categories.length > 1
    }

    async function setNewCategory() {
        let r: SimpleFetchResult

        updatingCategory = true
        try {
            if (selectCategory == null) {
                r = await simpleFetch('DELETE', `/json/domain/1/dcat/${domain.owner}/domain/${Domain.idna}`)
            } else {
                r = await simpleFetch('PUT', `/json/domain/1/dcat/${domain.owner}/domain/${Domain.idna}/${selectCategory.id}`)
            }
        } catch (e: any) {
            r = e
        }

        updatingCategory = false
        if (!r || !r.data || !r.data.success) {
            notify({
                type: 'danger',
                title: $_('Configuration de catégorie'),
                text: $_('Une erreur est survenue lors de la configuration.'),
            })
            return
        }

        categories = r.data.categories
        domainCategory = r.data.category
        selectCategory = r.data.category

        categoriesReady = categories.length > 1
    }

    onMount(() => {
        loadCategory()
    })
</script>

<style>
    .domain-groups {
        display: grid;
        grid-template-rows: 1fr;
        grid-gap: 30px;

        grid-template-columns: 1fr;
        grid-template-areas: 'exp' 'general' 'toggles' 'ops';

        /* margin: 30px 0; */
    }
    @media (min-width: 900px) and (max-width: 1599px) {
        .domain-groups {
            grid-template-columns: 1fr 1fr;
            grid-template-areas: 'exp general' 'exp general' 'toggles ops';
        }
    }
    @media (min-width: 1600px) {
        .domain-groups {
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-areas: 'exp toggles ops' 'general toggles ops';
        }
    }
    .domain-group-expiration {
        grid-area: exp;
    }
    .domain-group-general {
        grid-area: general;
    }
    .domain-group-toggles {
        grid-area: toggles;
    }
    .domain-group-ops {
        grid-area: ops;
    }

    .domain-group.card {
        margin-bottom: 0;
    }

    /* .dg-title {
        font-weight: bold;
        font-size: 1.1em;
        border-bottom: 1px solid #d0d0d0;
    } */
    .dg-property {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
    }

    .dg-property.dg-category {
        grid-template-columns: 1fr 13em 2.25em;
        grid-gap: 0;
    }
    .dg-property.dg-category.tog-w-action {
        grid-template-columns: 1fr 11em 1.5em 2.75em;
        grid-gap: 0;
    }
    .dg-property.dg-category > .tog-label {
        padding-right: 1em;
    }

    .tog-buttons :global(.dropdown) {
        width: 100%;
    }
    .tog-buttons :global(.dropdown-toggle) {
        width: 100%;
    }

    .tog-w-action :global(.dropdown) :global(.btn) {
        border-right: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
    .tog-e-action .btn {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    .tog-buttons.tog-empty button {
        width: 100%;
    }
</style>

<div class="domain-blocks-container">
    <div class="domain-groups">
        <div class="domain-group card domain-group-expiration">
            <div class="dg-title card-title text-primary">{$_('Expiration')}</div>
            <div class="dg-properties card-body">
                {#if $status == 'ready'}
                    <div class="dg-property domain-info info-align-top">
                        <div class="info-label">{$_('Expiration')}</div>
                        <div class="info-value" class:text-danger={Domain.expirationStatus == 'imminent'} class:text-warning={Domain.expirationStatus == 'soon'}>
                            <Date value={domain.expiration} format="h-table-s" />
                        </div>
                    </div>
                    {#if domain.real_expiration && domain.real_expiration.toMillis() != domain.expiration.toMillis()}
                        <div class="dg-property domain-info info-align-top">
                            <div class="info-label">{$_('Expiration réelle')}</div>
                            <div class="info-value">
                                <Date value={domain.real_expiration} format="h-table-s" />
                                <Icon inline icon="fa:exclamation-triangle" />
                            </div>
                        </div>
                    {/if}
                    {#if domain.fake_expiration && domain.real_expiration && domain.fake_expiration > domain.real_expiration}
                        <div class="dg-property domain-info info-align-top">
                            <div class="info-label">{$_('Expiration administrative')}</div>
                            <div class="info-value">
                                <Date value={domain.fake_expiration} format="h-table-s" />
                                <Icon inline icon="fa:exclamation-triangle" />
                            </div>
                        </div>
                    {/if}
                    <div class="dg-property domain-info info-align-top">
                        <div class="info-label">{$_('Stage of life')}</div>
                        <div class="info-value" class:text-danger={!domain.stage_of_life || domain.stage_of_life.startsWith('pending')} class:text-warning={domain.stage_of_life && domain.stage_of_life.endsWith('Period')}>
                            <span class="sol">{domain.stage_of_life}</span>
                        </div>
                    </div>
                    <div class="dg-property domain-info info-align-top">
                        <div class="info-label">{$_('Stage of life (end)')}</div>
                        <div class="info-value">
                            {#if !domain.stage_of_life}
                                <span class="sol-end no-date">{$_('unknown')}</span>
                            {:else}
                                <Date value={domain.stage_of_life_end} format="h-table-s" />
                            {/if}
                        </div>
                    </div>
                    {#if domain.unactivated_years && domain.unactivated_years > 0}
                        <div class="dg-property domain-toggle">
                            <div class="tog-label"><span>{$_('Unactivated years')}</span></div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button class="btn btn-secondary minimal" type="button" disabled>{domain.unactivated_years}</button>
                                    <button class="btn btn-secondary" type="button" class:btn-danger={missingYearCanBeActivated} disabled={!missingYearCanBeActivated || missingYearWorking} onclick={() => activateMissingYear()}>
                                        {#if missingYearCanBeActivated}{$_('Activate 1 year')}
                                        {:else}<Date value={missingYearCanBeActivatedFrom} format="h-table-s" />
                                        {/if}
                                    </button>
                                </div>
                            </div>
                        </div>
                    {/if}
                {/if}
            </div>
        </div>
        <div class="domain-group card domain-group-general">
            <div class="dg-title card-title text-primary">{$_('General informations')}</div>
            <div class="dg-properties card-body">
                {#if $status == 'ready'}
                    <div class="dg-property domain-info">
                        <div class="info-label">{$_('System')}</div>
                        <div class="info-value">{domain.system == 'cira' ? 'CIRA' : domain.system == 'opensrs' ? 'OpenSRS' : domain.system == 'webnames' ? 'Webnames' : domain.system}</div>
                    </div>
                    {#if Domain.idna !== Domain.unicode}
                        <div class="dg-property domain-info">
                            <div class="info-label">IDNA</div>
                            <div class="info-value">{Domain.idna}</div>
                        </div>
                    {/if}
                    <div class="dg-property domain-info">
                        <div class="info-label">{$_('Owner')}</div>
                        <div class="info-value">
                            <a class="btn btn-primary btn-sm" use:sanchor href={spath(`/wdom-user/${domain.owner}`)}>{ownerName ? ownerName : '…'}</a>
                        </div>
                    </div>
                    {#if accountingComment}
                        <div class="dg-property domain-info">
                            <div class="info-label">Acomba</div>
                            <div class="info-value">
                                {accountingComment}
                            </div>
                        </div>
                    {/if}
                    {#if domain.system == 'cira' && Domain.raw.bundle_siblings.length > 1}
                        <div class="dg-property domain-info info-align-top">
                            <div class="info-label">{$_('Bundle')}</div>
                            <div class="info-value">
                                <div>Already owned:</div>
                                <div>
                                    {#each Domain.raw.bundle_siblings as sibling}
                                        {#if sibling.idna != domain.idna}
                                            <span class="enum"><a use:sanchor href={spath(`/domain/${sibling.idna}`)}>{sibling.idna}</a></span>
                                        {/if}
                                    {/each}
                                </div>
                            </div>
                        </div>
                    {/if}
                    <div class="dg-property domain-toggle">
                        <div class="tog-label">{$_('EPP / Auth code')}</div>
                        <div class="tog-buttons">
                            <div class="btn-group btn-group-sm">
                                {#if epp === undefined}
                                    <button type="button" class="btn btn-secondary" onclick={() => loadEpp()} disabled={eppLoading}>{$_('Obtain')}</button>
                                {:else}
                                    <span><input type="text" readonly class="form-control input-sm" class:is-warning={epp !== null} class:is-invalid={epp === null} value={epp === null ? 'error' : epp} /></span>
                                {/if}
                            </div>
                        </div>
                        <div class="tog-progress">
                            {#if eppLoading}<span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>{/if}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
        <div class="domain-group card domain-group-toggles">
            <div class="dg-title card-title text-primary">Propriétés</div>
            <div class="dg-properties card-body">
                {#if $status == 'ready'}
                    <div class="dg-property domain-toggle">
                        <div class="tog-label">WHOIS</div>
                        <div class="tog-buttons">
                            <div class="btn-group btn-group-sm">
                                <button
                                    type="button"
                                    class="btn"
                                    class:btn-outline-secondary={whoisPrivacyActive}
                                    class:btn-secondary={!whoisPrivacyActive}
                                    class:active={!whoisPrivacyActive}
                                    disabled={!whoisPrivacyActive || domain.status != 'ready' || whoisPrivacyTaskRunning || !domain.whois_privacy_mutable || !Domain.whoisPrivacyAllowed}
                                    onclick={() => {
                                        if (Domain.whoisPrivacyPrice == 0) {
                                            Domain.clearPrivacyNoConfirm()
                                        } else {
                                            confirmModal = 'clearWhois'
                                        }
                                    }}>Public</button
                                >
                                <button
                                    type="button"
                                    class="btn"
                                    class:btn-outline-secondary={!whoisPrivacyActive}
                                    class:btn-secondary={whoisPrivacyActive}
                                    class:active={whoisPrivacyActive}
                                    disabled={whoisPrivacyActive || domain.status != 'ready' || whoisPrivacyTaskRunning || !domain.whois_privacy_mutable || !Domain.whoisPrivacyAllowed}
                                    onclick={() => {
                                        if (Domain.whoisPrivacyPrice == 0) {
                                            Domain.setWhoisPrivacy()
                                        } else {
                                            confirmModal = 'setWhois'
                                        }
                                    }}>Privé</button
                                >
                            </div>
                        </div>
                        <div class="tog-progress">
                            {#if whoisPrivacyTaskRunning}
                                <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                            {/if}
                        </div>
                    </div>
                    <div class="dg-property domain-toggle">
                        <div class="tog-label" class:text-warning={!locked}>{$_('Locking (Transfer)')}</div>
                        <div class="tog-buttons">
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn" class:btn-outline-secondary={locked} disabled={!locked || registrarLockTaskRunning} class:active={!locked} class:btn-warning={!locked} onclick={() => Domain.unlock()}>{$_('Allowed')}</button>
                                <button type="button" class="btn" class:btn-outline-secondary={!locked} disabled={locked || registrarLockTaskRunning} class:active={locked} class:btn-secondary={locked} onclick={() => Domain.lock()}>{$_('Blocked')}</button>
                            </div>
                        </div>
                        <div class="tog-progress">
                            {#if registrarLockTaskRunning}
                                <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                            {/if}
                        </div>
                    </div>
                    {#if domain.system == 'cira'}
                        <div class="dg-property domain-toggle">
                            <div class="tog-label" class:text-danger={clientHold}>Client hold</div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button type="button" class="btn" class:btn-outline-secondary={clientHold} class:btn-secondary={!clientHold} disabled={!clientHold || clientHoldTaskRunning} class:active={!clientHold} onclick={() => Domain.setCiraStatus('clientHold', false)}>{$_('Inactive')}</button>
                                    <button type="button" class="btn" class:btn-outline-secondary={!clientHold} disabled={clientHold || clientHoldTaskRunning} class:active={clientHold} class:btn-danger={clientHold} onclick={() => Domain.setCiraStatus('clientHold', true)}>{$_('Active')}</button>
                                </div>
                            </div>
                            <div class="tog-progress">
                                {#if clientHoldTaskRunning}
                                    <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                                {/if}
                            </div>
                        </div>
                        <div class="dg-property domain-toggle">
                            <div class="tog-label" class:text-danger={clientDeleteProhibited}>Client delete</div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button type="button" class="btn" class:btn-outline-secondary={clientDeleteProhibited} class:btn-secondary={!clientDeleteProhibited} disabled={!clientDeleteProhibited || clientDeleteProhibitedTaskRunning} class:active={!clientDeleteProhibited} onclick={() => Domain.setCiraStatus('clientDeleteProhibited', false)}>{$_('Allowed')}</button>
                                    <button type="button" class="btn" class:btn-outline-secondary={!clientDeleteProhibited} disabled={clientDeleteProhibited || clientDeleteProhibitedTaskRunning} class:active={clientDeleteProhibited} class:btn-danger={clientDeleteProhibited} onclick={() => Domain.setCiraStatus('clientDeleteProhibited', true)}>{$_('Blocked')}</button>
                                </div>
                            </div>
                            <div class="tog-progress">
                                {#if clientDeleteProhibitedTaskRunning}
                                    <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                                {/if}
                            </div>
                        </div>
                        <div class="dg-property domain-toggle">
                            <div class="tog-label" class:text-danger={clientRenewProhibited}>Client renew</div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button type="button" class="btn" class:btn-outline-secondary={clientRenewProhibited} disabled={!clientRenewProhibited || clientRenewProhibitedTaskRunning} class:active={!clientRenewProhibited} class:btn-secondary={!clientRenewProhibited} onclick={() => Domain.setCiraStatus('clientRenewProhibited', false)}>{$_('Allowed')}</button>
                                    <button type="button" class="btn" class:btn-outline-secondary={!clientRenewProhibited} disabled={clientRenewProhibited || clientRenewProhibitedTaskRunning} class:active={clientRenewProhibited} class:btn-danger={clientRenewProhibited} onclick={() => Domain.setCiraStatus('clientRenewProhibited', true)}>{$_('Blocked')}</button>
                                </div>
                            </div>
                            <div class="tog-progress">
                                {#if clientRenewProhibitedTaskRunning}
                                    <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                                {/if}
                            </div>
                        </div>
                        <div class="dg-property domain-toggle">
                            <div class="tog-label" class:text-danger={clientUpdateProhibited}>Client update</div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button type="button" class="btn" class:btn-outline-secondary={clientUpdateProhibited} class:btn-secondary={!clientUpdateProhibited} disabled={!clientUpdateProhibited || clientUpdateProhibitedTaskRunning} class:active={!clientUpdateProhibited} onclick={() => Domain.setCiraStatus('clientUpdateProhibited', false)}>{$_('Allowed')}</button>
                                    <button type="button" class="btn" class:btn-outline-secondary={!clientUpdateProhibited} disabled={clientUpdateProhibited || clientUpdateProhibitedTaskRunning} class:active={clientUpdateProhibited} class:btn-danger={clientUpdateProhibited} onclick={() => Domain.setCiraStatus('clientUpdateProhibited', true)}>{$_('Blocked')}</button>
                                </div>
                            </div>
                            <div class="tog-progress">
                                {#if clientUpdateProhibitedTaskRunning}
                                    <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                                {/if}
                            </div>
                        </div>
                    {/if}
                {/if}
            </div>
        </div>
        <div class="domain-group card domain-group-ops">
            <div class="dg-title card-title text-primary">{$_('Operations')}</div>
            <div class="dg-properties card-body">
                {#if $status == 'ready'}
                    <div class="dg-property domain-toggle">
                        <div class="tog-label">{$_('Update')}</div>
                        <div class="tog-buttons">
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-secondary" onclick={() => doSync()} disabled={localSync || $syncTaskRunning}>{$_('Update local data')}</button>
                            </div>
                        </div>
                        <div class="tog-progress">
                            {#if localSync || $syncTaskRunning}<span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>{/if}
                        </div>
                    </div>
                    <div class="dg-property domain-info">
                        <div class="info-label">Tasks</div>
                        <div class="info-value"><a use:sanchor href={spath(`/tasks?affected_id=${domain.idna}`)} class="btn btn-secondary btn-sm">{$_('Domain’s tasks')}</a></div>
                    </div>

                    <div class="dg-property domain-tmp-exp">
                        {#if specialOpen}
                            <div class="tog-pop">
                                <PopEntry close={() => (specialOpen = false)}>
                                    <div class="card">
                                        <div class="card-header text-bg-primary">{$_('Expiration temporaire')}</div>
                                        <div class="card-body">
                                            <MaskInput
                                                class="form-control"
                                                alwaysShowMask={false}
                                                mask="0000-00-00"
                                                placeholder="YYYY-MM-DD"
                                                value={specialValueEdit}
                                                on:change={({ detail }) => {
                                                    console.log('On change..')
                                                    specialValueEdit = detail.inputState.maskedValue
                                                }}
                                                on:enter={() => {
                                                    if (specialIsValid(specialValueEdit)) saveFakeExpiration(specialValueEdit)
                                                }}
                                            />
                                        </div>
                                        <div class="card-footer d-flex gap-2 justify-content-end">
                                            <button type="button" class="btn btn-sm btn-secondary" onclick={() => (specialOpen = false)}>{$_('Fermer')}</button>
                                            {#if special.expiration}
                                                <button type="button" class="btn btn-sm btn-secondary" onclick={() => saveFakeExpiration(undefined)}>{$_('Vider')}</button>
                                            {/if}
                                            <button type="button" class="btn btn-sm btn-primary" disabled={!specialIsValid(specialValueEdit)} onclick={() => saveFakeExpiration(specialValueEdit)}>{$_('Appliquer')}</button>
                                        </div>
                                    </div>
                                </PopEntry>
                            </div>
                        {/if}
                        <div class="tog-label">{$_('Expiration temporaire')}</div>
                        <div class="tog-buttons">
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-outline-secondary" disabled={specialWorking} onclick={() => openSpecial()}>
                                    {#if specialValue}
                                        {specialValue}
                                    {:else}
                                        {$_('Configurer')}
                                    {/if}
                                </button>
                            </div>
                        </div>
                        <div class="tog-progress">
                            {#if specialWorking}<span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>{/if}
                        </div>
                    </div>

                    {#if $userGroups.has('global-domain-admin') && $userGroups.has('global-domain-delete')}
                        <div class="dg-property domain-toggle">
                            <div class="tog-label">{$_('Delete')}</div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button type="button" class="btn btn-warning" disabled={deletingDomain} onclick={() => (confirmModal = 'delete')}>{$_('Delete')}</button>
                                </div>
                            </div>
                            <div class="tog-progress">
                                {#if deletingDomain}<span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>{/if}
                            </div>
                        </div>
                    {/if}
                    {#if $userGroups.has('global-ecom-admin')}
                        <div class="dg-property domain-toggle">
                            <div class="tog-label">{$_('Create a new order')}</div>
                            <div class="tog-buttons">
                                <div class="btn-group btn-group-sm">
                                    <button type="button" class="btn btn-secondary" disabled={creatingOrder} onclick={() => createOrder()}>{$_('Create a new order')}</button>
                                    <button type="button" class="btn btn-outline-secondary" style="flex: 0" disabled={creatingOrder} onclick={() => createOrder(true)}>{'D'}</button>
                                </div>
                            </div>
                            <div class="tog-progress">
                                {#if creatingOrder}<span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>{/if}
                            </div>
                        </div>
                    {/if}

                    <div class="dg-property domain-tags">
                        <div class="tag-label">Tags</div>
                        <div class="tag-tags">
                            {#if sortedTags != null && sortedTags.length}
                                {#each sortedTags as tag}
                                    <div class="tag">
                                        <button type="button" class="btn btn-sm btn-outline-primary" data-tooltip={$_('Enlever « {tag} »', { tag: tag })} onclick={() => preRemoveTag(tag)}>{tag}</button>
                                    </div>
                                {/each}
                            {:else}
                                <div class="notag">Aucun tag</div>
                            {/if}
                            <!-- <div class="tag-sep"></div> -->
                            <div class="tag-add">
                                <button type="button" class="btn btn-sm btn-primary" data-tooltip={$_('Ajouter un tag')} onclick={() => preAddTag()}>Ajouter</button>
                            </div>
                        </div>
                    </div>

                    <div class="dg-property domain-toggle dg-category" class:tog-w-action={categoryDirty}>
                        <div class="tog-label">Catégorie</div>
                        {#if categoriesReady == null}
                            <div class="tog-progress">
                                <span class="text-warning"><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
                            </div>
                        {:else if categoriesReady}
                            <div class="tog-buttons">
                                <Dropdown
                                    size="sm"
                                    alignRight={true}
                                    onSelect={(e) => {
                                        if (e == null) selectCategory = undefined
                                        else
                                            for (const i of categories) {
                                                if (i.id == e) {
                                                    selectCategory = i
                                                    break
                                                }
                                            }
                                    }}
                                    placeholder={$_('Non-catégorisé')}
                                    selected={selectCategory != null ? selectCategory.id : undefined}
                                    items={categories}
                                    text={(sitem) => {
                                        return sitem.id == null ? $_('Non-catégorisé') : sitem.name
                                    }}
                                    selectedText={(sitem) => {
                                        return sitem.id == null ? $_('Non-catégorisé') : sitem.name
                                    }}
                                    id={'id'}
                                    disabled={updatingCategory}
                                />
                            </div>
                            {#if categoryDirty}
                                <div class="tog-e-action">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-primary"
                                        disabled={updatingCategory}
                                        onclick={() => {
                                            setNewCategory()
                                        }}
                                    >
                                        <Icon inline icon="mdi:check-bold" />
                                    </button>
                                </div>
                            {/if}
                        {:else}
                            <div class="tog-buttons tog-empty">
                                <button type="button" class="btn btn-sm btn-outline-secondary" disabled={true}>Sans catégorie</button>
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (confirmModal !== false && document.documentElement.classList.contains('modal-open')) {
                confirmModal = false
            }
        }
    }}
/>

{#if confirmModal == 'clearWhois' || confirmModal == 'setWhois'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }} onclick={() => (confirmModal = false)} aria-hidden="true">
        <div
            class="modal-dialog"
            onclick={(e) => {
                e.stopPropagation()
            }}
            aria-hidden="true"
        >
            <div class="modal-content">
                <div class="modal-title text-primary">
                    <div>{$_('Deactivate WHOIS Privacy')}</div>
                </div>
                <div class="modal-body">
                    <p>Le WHOIS pour cette extension est de <IntCurrency value={Domain.whoisPrivacyPrice} />.</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" onclick={() => (confirmModal = false)} disabled={togglingWhois}>{$_('Cancel')}</button>
                    {#if confirmModal == 'clearWhois'}
                        <button
                            type="button"
                            class="btn btn-primary"
                            onclick={async () => {
                                togglingWhois = true
                                await Domain.clearPrivacyNoConfirm()
                                togglingWhois = false
                                confirmModal = false
                            }}
                            disabled={togglingWhois}
                        >
                            {#if togglingWhois}
                                <div class="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></div>
                            {/if}
                            Déactiver WHOIS Privacy
                        </button>
                    {:else}
                        <button
                            type="button"
                            class="btn btn-primary"
                            onclick={async () => {
                                togglingWhois = true
                                await Domain.setWhoisPrivacy()
                                togglingWhois = false
                                confirmModal = false
                            }}
                            disabled={togglingWhois}
                        >
                            {#if togglingWhois}
                                <div class="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></div>
                            {/if}
                            Activer WHOIS Privacy
                        </button>
                    {/if}
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}
{#if confirmModal == 'delete'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>Détruire le domaine</div>
                </div>
                <div class="modal-body">
                    <p>Cette opération enlève le domaine de WebDomaines, mais n’effectue aucune opération au registre.</p>
                    <textarea class="form-control" bind:value={deleteReason}></textarea>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" onclick={() => (confirmModal = false)}>{$_('Cancel')}</button>
                    <button type="button" class="btn btn-danger" onclick={() => deleteDomain()}>{$_('Delete')}{cleanDeleteReason != '' ? ' + raison' : ''}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (confirmModal = false)} aria-hidden="true"></div>
    </div>
{/if}
{#if confirmModal == 'tag-remove'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>Enlever un tag</div>
                </div>
                <div class="modal-body">
                    <p>Enlever le <em>tag</em> « {tagToRemove} » ?</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" disabled={tagWorking} onclick={() => (confirmModal = false)}>{$_('Cancel')}</button>
                    <button type="button" class="btn btn-danger" disabled={tagWorking} onclick={() => removeTag(tagToRemove)}>{$_('Delete')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (confirmModal = false)} aria-hidden="true"></div>
    </div>
{/if}
{#if confirmModal == 'tag-add'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    addTag(tagToAdd.trim())
                }}
            >
                <div class="modal-content">
                    <div class="modal-header text-bg-primary">
                        <div>Ajouter un tag</div>
                    </div>
                    <div class="modal-body">
                        <input type="text" class="form-control" bind:value={tagToAdd} bind:this={tagInputElement} disabled={tagWorking} />
                        <div class="help-block">Le <em>tag</em> sera ajouté tel quel incluant la casse et caractères spéciaux. La recherche tiendra compte de ces particularités.</div>
                        {#if frequentTags && frequentTags.length}
                            <div class="mt-3">
                                <div>Tags fréquents:</div>
                                <div class="freq-tags">
                                    {#each frequentTags as tag}
                                        <button type="button" class="btn btn-sm btn-link" onclick={() => (tagToAdd = tag.id)}>{tag.id} ({tag.freq})</button>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" disabled={tagWorking} onclick={() => (confirmModal = false)}>{$_('Cancel')}</button>
                        <button type="submit" class="btn btn-danger" disabled={tagWorking || tagToAdd.trim() == ''}>{$_('Add')}</button>
                    </div>
                </div>
            </form>
        </div>
        <div class="modal-backdrop" onclick={() => (confirmModal = false)} aria-hidden="true"></div>
    </div>
{/if}
